import {GtmEventModel} from "./gtmEventModel";
import {TRACKING_EVENT_TYPE} from "../../tracking";
import {CustomEventTrackingData} from "./customEventTrackingData";
import {ShopTrackingEvents} from "./events";
import {GtmItemModel} from "./gtmItemModel";
import {DataLayerHelper} from "./dataLayerHelper";

export class GtmEventFactory {

    /**
     * @type {string}
     */
    static DATA_PARAM_TRACKING_EVENT_TYPE = 'trackingEventType';

    /**
     * @type {string}
     */
    static DATA_PARAM_DATA_LAYER_IDENTIFIER = 'trackingDataLayerIdentifier';

    /**
     * @type {string}
     */
    static DATA_PARAM_CLICK_EVENT_LABEL = 'label';

    /**
     * @type {string}
     */
    static DATA_PARAM_CLICK_EVENT_URL = 'url';

    /**
     * @type {string}
     */
    static DATA_PARAM_SEARCH_QUERY = 'searchQuery';

    /**
     * @type {string}
     */
    static DATA_PARAM_COUPON_CODE = 'coupon_code';

    /**
     * @type {{}}
     */
    data = {};

    /**
     * @param data
     */
    constructor(data = {}) {
        this.data = data;
    }

    /**
     * @param contextualData {Object}
     * @returns {GtmEventModel}
     */
    create(contextualData = {}) {
        const trackingEventType = this.getTrackingEventType(contextualData);
        const dataLayerIdentifier = this.getDataLayerIdentifier(contextualData);

        let gtmEventModel = null;

        if (trackingEventType === TRACKING_EVENT_TYPE.CUSTOMFROMSHOP) {

            gtmEventModel = this.createFromCustomEventInShop(contextualData);

        } else {

            const dataLayerIdentifier = this.getDataLayerIdentifier(contextualData);

            gtmEventModel = this.createBasedOnDataLayerIdentifier(dataLayerIdentifier);
        }

        if (gtmEventModel === null) {

            gtmEventModel = new GtmEventModel();
        }

        return gtmEventModel;
    }

    /**
     * @param contextualData {Object}
     * @returns {*|string}
     */
    getTrackingEventType(contextualData) {

        return contextualData[GtmEventFactory.DATA_PARAM_TRACKING_EVENT_TYPE] ?? '';
    }

    /**
     * @returns {*|number}
     */
    getQuantityFromData() {

        return this.data[ShopTrackingEvents.CUSTOM_EVENT_PARAM_PRODUCT_QUANTITY] ?? 1
    }

    /**
     * @returns {*|null}
     */
    getCustomEventTrackingData() {

        let customEventTrackingData;

        if (this.data === null) {
            return null;
        }

        if (typeof this.data !== 'object') {
            return null;
        }

        if (!this.data.hasOwnProperty(ShopTrackingEvents.CUSTOM_EVENT_PARMETER_TRACKING_DATA)) {
            return null;
        }

        if (this.data[ShopTrackingEvents.CUSTOM_EVENT_PARMETER_TRACKING_DATA] === null ||
            this.data[ShopTrackingEvents.CUSTOM_EVENT_PARMETER_TRACKING_DATA] === undefined ||
            this.data[ShopTrackingEvents.CUSTOM_EVENT_PARMETER_TRACKING_DATA] === ''
        ) {
            return null;
        }

        customEventTrackingData = JSON.parse(this.data[ShopTrackingEvents.CUSTOM_EVENT_PARMETER_TRACKING_DATA])

        if (customEventTrackingData === null ||
            customEventTrackingData === undefined ||
            customEventTrackingData === ''
        ) {
            return null;
        }

        customEventTrackingData = new CustomEventTrackingData(customEventTrackingData);

        if (customEventTrackingData.isValid() === false) {

            return null;
        }

        return customEventTrackingData;
    }

    /**
     * @param contextualData {Object}
     */
    createFromCustomEventInShop(contextualData) {

        const customEventTrackingData = this.getCustomEventTrackingData();

        if (customEventTrackingData === null) {

            console.trace('Custom event tracking data is not valid');
        }

        const quantity = this.getQuantityFromData()
        const gtmEventModel = new GtmEventModel();

        gtmEventModel.addItem(
            this.getGtmItemForTrackingDataAndQuantity(
                quantity,
                customEventTrackingData
            )
        )

        return gtmEventModel;
    }

    /**
     * @param dataLayerIdentifier {string}
     * @returns {GtmEventModel}
     */
    createBasedOnDataLayerIdentifier(dataLayerIdentifier) {
        return this.createEcommerceDataFromData(this.data);
    }

    /**
     * @param data {Object}
     * @returns {GtmEventModel|{}}
     */
    createEcommerceDataFromData(data) {
        if (data === null) {
            console.trace('No data provided for gtm event creation');

            return {};
        }

        const gtmEvent = new GtmEventModel();
        const amount = data['amount'] ?? 0;
        const coupon = data['coupon'] ?? '';
        const currency = data['currency'] ?? 'EUR';
        let shipping = '';
        let payment = '';

        if (data.hasOwnProperty('shipping')) {

            shipping = data['shipping'];
        }

        if (data.hasOwnProperty('payment')) {

            payment = data['payment'];
        }

        gtmEvent.currency = currency;
        gtmEvent.value = amount;
        gtmEvent.coupon = coupon;
        gtmEvent.shipping_tier = shipping;
        gtmEvent.payment_type = payment;

        // Set items
        if (data.hasOwnProperty('items') &&
            data['items'].length > 0
        ) {
            gtmEvent.items = this.getItemsFromDataAsGtmItems(data);

        } else {

            gtmEvent.items.push(this.getGtmItemByData(data));
        }

        // We can add more data contextually but for now we are good with this
        return gtmEvent;
    }

    /**
     *
     * @param quantity {number}
     * @param customEventTrackingData {CustomEventTrackingData}
     * @returns {GtmItemModel}
     */
    getGtmItemForTrackingDataAndQuantity(
        quantity,
        customEventTrackingData
    ) {
        const item = new GtmItemModel();

        item.quantity = quantity;
        item.item_id = customEventTrackingData.id;
        item.item_name = customEventTrackingData.name;
        item.price = customEventTrackingData.price;
        item.item_category = customEventTrackingData.category;

        return item;
    }

    /**
     * @param contextualData {Object}
     * @returns {*|string}
     */
    getDataLayerIdentifier(contextualData) {

        return contextualData[GtmEventFactory.DATA_PARAM_DATA_LAYER_IDENTIFIER] ?? '';
    }

    /**
     * @param contextualData {Object}
     * @returns {{}}
     */
    createOnTheFly(contextualData) {
        const trackingEventType = this.getTrackingEventType(contextualData);
        const dataLayerIdentifier = this.getDataLayerIdentifier(contextualData);

        // On the fly click event gtm event object creation
        if (trackingEventType === TRACKING_EVENT_TYPE.CLICKLISTENERSHOP) {

            return this.createFromClickInformation(dataLayerIdentifier);
        }

        // On the fly submit event gtm event object creation
        if (trackingEventType === TRACKING_EVENT_TYPE.SUBMITLISTENERSHOP) {

            // Shop search usage
            if (dataLayerIdentifier === DataLayerHelper.SEARCH_EVENT ||
                dataLayerIdentifier === DataLayerHelper.VIN_SEARCH_EVENT
            ) {
                return {
                    search_term: this.data[GtmEventFactory.DATA_PARAM_SEARCH_QUERY] ?? ''
                }
            }

            // Shop coupon usage
            if (dataLayerIdentifier === DataLayerHelper.APPLY_COUPON_EVENT) {
                return {
                    coupon_code: this.data[GtmEventFactory.DATA_PARAM_COUPON_CODE] ?? ''
                }
            }
        }

        if (dataLayerIdentifier === DataLayerHelper.CHECKOUT_STEP_1_EVENT ||
            dataLayerIdentifier === DataLayerHelper.CHECKOUT_STEP_2_EVENT ||
            dataLayerIdentifier === DataLayerHelper.CHECKOUT_STEP_3_EVENT
        ) {
            let ecommerceData = this.data['ecommerce'] ?? null;
            let step = 0;

            if (ecommerceData !== null) {
                ecommerceData = this.createEcommerceDataFromData(ecommerceData)
            }

            if (dataLayerIdentifier === DataLayerHelper.CHECKOUT_STEP_1_EVENT) {

                step = 1;
            }
            if (dataLayerIdentifier === DataLayerHelper.CHECKOUT_STEP_2_EVENT) {

                step = 2;
            }
            if (dataLayerIdentifier === DataLayerHelper.CHECKOUT_STEP_3_EVENT) {

                step = 3;
            }

            /**
             * Be aware here we're wrapping the ecommerce key directly into the event object!
             * This is normally decided by the dataLayer isEcommerceEvent method where the name of the
             * event is checked against the ecommerce events.
             */
            return {
                step: step,
                ecommerce: ecommerceData?.getEventData() ?? {},
                country: this.data['country'] ?? '',
                shipping: this.data['shipping'] ?? '',
                payment: this.data['payment'] ?? '',
            }
        }

        if (dataLayerIdentifier === DataLayerHelper.REMOVE_FROM_CART_EVENT) {
            return this.createEcommerceDataFromData(this.data).getEventData() ?? {}
        }
    }

    /**
     * @param dataLayerIdentifier {string}
     * @returns {{event_label: (*|string), event_link: (*|string)}}
     */
    createFromClickInformation(dataLayerIdentifier) {

        return {
            event_label: this.data[GtmEventFactory.DATA_PARAM_CLICK_EVENT_LABEL] ?? '',
            event_link: this.data[GtmEventFactory.DATA_PARAM_CLICK_EVENT_URL] ?? '',
        }
    }

    /**
     * @param data {Object}
     * @returns {*[]}
     */
    getItemsFromDataAsGtmItems(data) {
        const items = [];

        data['items'].forEach(item => {
            const gtmItem = this.getGtmItemByData(item)

            items.push(gtmItem);
        });

        return items;
    }

    /**
     * @param data
     * @returns {GtmItemModel}
     */
    getGtmItemByData(data) {

        const gtmItem = new GtmItemModel();

        gtmItem.item_id = data['id'] ?? '';
        gtmItem.item_name = data['name'] ?? '';
        gtmItem.price = data['price'] ?? 0;
        gtmItem.item_category = data['category'] ?? '';
        gtmItem.quantity = data['quantity'] ?? 1;

        return gtmItem;
    }
}